import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {useCountries} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {TOOL_NAME} from 'src/modules/bulk-update/add-attendees/constants';
import {FIELD} from 'src/modules/bulk-update/add-attendees/steps/upload-file-step/constants';
import {getUsersByEmails} from 'src/openapi/admin/apis/GetUserByEmailApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';

import {
    selectBulkUserImportFile,
    selectBulkUserImportImportSettings,
    selectBulkUserImportTargetAccount,
    setTemplateData,
    setValidateFileStep,
} from '../../services/add-attendees-bulk.slice';
import {validateColumnValue} from '../../services/utils';
import {
    INVALID_CITY_MESSAGE,
    INVALID_EMAIL_MESSAGE,
    INVALID_FIRST_NAME_MESSAGE,
    INVALID_JOB_TITLE_MESSAGE,
    INVALID_LAST_NAME_MESSAGE,
} from '../validate-file-step/constants';

import type {BulkImportUserType, UserBulkImportFileType} from '../../services/typedef';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {ExistedUserInfo} from 'src/openapi/admin/models/ExistedUserInfo';


export const UploadFileControls = () => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {countries} = useCountries();
    const {bulkSubmit} = useEventTracking();

    const location = useLocation();
    const prevPath = location.state?.prevPath;
    const navigate = useNavigate();
    const [isParsing, setIsParsing] = useState(false);

    const conferenceAccount = useSelector(selectBulkUserImportTargetAccount);
    const importSettings = useSelector(selectBulkUserImportImportSettings);
    const file = useSelector(selectBulkUserImportFile);
    const cancel = useCallback(() => {
        bulkSubmit({toolName: TOOL_NAME, submit: false});
        if (prevPath) {
            navigate(-1);
        } else {
            navigate('/');
        }
    }, [prevPath, bulkSubmit, navigate]);

    const getUsersFromSystem = async (data: UserBulkImportFileType[]) => {
        const map = new Map();
        const userEmails = data.map(item => item[FIELD.EMAIL]?.toLowerCase());
        if (userEmails && userEmails.length > 0) {
            const requestBody = userEmails.filter(email => email) as string[];
            const response = await apiDispatch(getUsersByEmails({requestBody}));
            if (response.payload.success) {
                response.payload.data.forEach(user => map.set(user.email, user));
            }
        }
        return map;
    };

    const getCountry = (searchTerm?: string): DictionaryItem | undefined => {
        if (!searchTerm || searchTerm === '') {
            return undefined;
        }
        return countries.find(country =>
            country.code.toLowerCase() === searchTerm.toLowerCase() ||
            country.description.toLowerCase() === searchTerm.toLowerCase(),
        );
    };

    const mapToBulkImportUserType = (
        data: UserBulkImportFileType[],
        usersFromSystem: Map<string, ExistedUserInfo>,
    ): BulkImportUserType[] => {
        const sanitizedData: UserBulkImportFileType[] = [];
        for (const item of data) {
            const email = item[FIELD.EMAIL]?.trim() ?? '';
            const sanitizedEmails = sanitizedData
                .map(sanitizedItem => sanitizedItem[FIELD.EMAIL]);

            if (email && !sanitizedEmails.includes(email)) {
                sanitizedData.push(item);
            }
        }

        return sanitizedData.map((item, index) => {
            const email = item[FIELD.EMAIL]?.toLowerCase() ?? '';
            const existUser = usersFromSystem.get(email);
            const id = existUser?.userId ?? index + 1;
            const firstName = existUser?.firstName ?? item[FIELD.FIRST_NAME] ?? '';
            const lastName = existUser?.lastName ?? item[FIELD.LAST_NAME] ?? '';
            const city = existUser?.city ?? item[FIELD.CITY] ?? '';
            const jobTitle = existUser?.jobTitle ?? item[FIELD.JOB_TITLE] ?? '';
            const notes = item[FIELD.NOTES] ?? '';
            const country = getCountry(existUser?.countryCode ?? item[FIELD.COUNTRY] ?? '') || null;

            return ({
                id,
                conferenceAccount,
                email: {
                    value: email,
                    validationError: validateColumnValue(FIELD.EMAIL, email)
                        ? INVALID_EMAIL_MESSAGE
                        : null,
                },
                firstName: {
                    value: firstName,
                    validationError: validateColumnValue(FIELD.FIRST_NAME, firstName)
                        ? INVALID_FIRST_NAME_MESSAGE
                        : null,
                },
                lastName: {
                    value: lastName,
                    validationError: validateColumnValue(FIELD.LAST_NAME, lastName)
                        ? INVALID_LAST_NAME_MESSAGE
                        : null,
                },
                jobTitle: {
                    value: jobTitle,
                    validationError: validateColumnValue(FIELD.JOB_TITLE, jobTitle)
                        ? INVALID_JOB_TITLE_MESSAGE
                        : null,
                },
                city: {
                    value: city,
                    validationError: validateColumnValue(FIELD.CITY, city)
                        ? INVALID_CITY_MESSAGE
                        : null,
                },
                notes: {
                    value: notes,
                    validationError: null,
                },
                importSettings,
                country,
                primaryAccount: existUser?.accountInfo || null,
            });
        });
    };

    const next = async () => {
        if (file) {
            setIsParsing(true);
            const data = mapToBulkImportUserType(file, await getUsersFromSystem(file));
            dispatch(setTemplateData(data));
            setIsParsing(false);
        } else {
            dispatch(setTemplateData(null));
        }

        dispatch(setValidateFileStep());
    };

    return (
        <Group>
            <Button variant="Type.SECONDARY" onClick={cancel}>Cancel</Button>
            <Button
                variant="Type.PRIMARY"
                busy={isParsing}
                disabled={!file || !conferenceAccount}
                onClick={next}>
                Continue
            </Button>
        </Group>
    );
};
